import get from '../../../../utilities/data/get';
import { getArrayFields, getNonArraySchema, showReviewField } from '../helpers';

/**
 * Shared review-page data helpers for accordion and flat layouts.
 * Render/view decisions stay in the chapter components.
 */

export function getReviewChapterTitle(chapterFormConfig, form) {
  const formData = form.data;
  let chapterTitle = chapterFormConfig.title;

  if (typeof chapterFormConfig.title === 'function') {
    chapterTitle = chapterFormConfig.title({
      formData,
      formConfig: form,
      onReviewPage: true,
    });
  }
  if (chapterFormConfig.reviewTitle) {
    chapterTitle = chapterFormConfig.reviewTitle;
  }

  return chapterTitle || '';
}

export function getReviewPageTitle(rawPageTitle, formData) {
  if (typeof rawPageTitle === 'function') {
    return rawPageTitle({ formData });
  }
  return rawPageTitle;
}

/**
 * Schema, uiSchema, data, and array fields for one review page.
 *
 * @param {Object} page - expanded page (pageKey, optional index/arrayPath)
 * @param {Object} pageState - form.pages[page.pageKey]
 * @param {Object} formData - form.data
 * @returns {{
 *   pageSchema: Object,
 *   pageUiSchema: Object,
 *   pageData: Object,
 *   arrayFields: Array,
 * }}
 */
export function getSchemaPageContent(page, pageState, formData) {
  if (page.showPagePerItem) {
    return {
      pageSchema: pageState.schema.properties[page.arrayPath].items[page.index],
      pageUiSchema: pageState.uiSchema[page.arrayPath].items,
      pageData: get([page.arrayPath, page.index], formData),
      arrayFields: [],
    };
  }

  // Array fields (growable tables) are split out of the main page and
  // rendered separately. ObjectField hides them in the main review section.
  const pageSchemaObjects = getNonArraySchema(
    pageState.schema,
    pageState.uiSchema,
  );

  return {
    pageSchema: pageSchemaObjects.schema,
    pageUiSchema: pageSchemaObjects.uiSchema,
    pageData: formData,
    arrayFields: getArrayFields(pageState, page),
  };
}

export function reviewTitlesMatch(a, b) {
  const normalize = value =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  const left = normalize(a);
  const right = normalize(b);
  return Boolean(left && left === right);
}

/**
 * Hide a page heading when it repeats the chapter title. Only when the
 * chapter has one expanded page. Same check the accordion review uses.
 */
export function shouldHideExpandedPageTitle(
  expandedPages,
  chapterTitle,
  pageTitle,
) {
  return (
    expandedPages?.length === 1 &&
    typeof pageTitle === 'string' &&
    (chapterTitle || '').toLowerCase() === pageTitle.toLowerCase()
  );
}

/**
 * A field is the page header when labelHeaderLevel is a real heading level.
 * An empty string means the field is a normal label.
 */
function fieldIsPageHeader(uiOptions = {}) {
  const level = uiOptions.labelHeaderLevel;
  return typeof level === 'string' && level !== '';
}

/**
 * Hide a field label on flat review when that field is the page header and
 * its label matches the page title. Exact match only. Accordion review keeps
 * the label. hideLabelOnReview still hides a label on either layout.
 */
export function shouldHideReviewFieldLabel(
  label,
  pageTitle,
  uiOptions = {},
  { flatReview = false } = {},
) {
  if (uiOptions.hideLabelOnReview) {
    return true;
  }

  return (
    Boolean(flatReview) &&
    fieldIsPageHeader(uiOptions) &&
    typeof label === 'string' &&
    typeof pageTitle === 'string' &&
    reviewTitlesMatch(label, pageTitle)
  );
}

export function getReviewFieldTitle(uiSchema, formData) {
  const reviewTitle = uiSchema?.['ui:options']?.reviewTitle;
  const raw = reviewTitle ?? uiSchema?.['ui:title'];
  if (typeof raw === 'function') {
    return raw(formData);
  }
  return typeof raw === 'string' ? raw : '';
}

export function getVisibleSimpleReviewFields(
  pageSchema,
  pageUiSchema,
  formData,
  formContext,
) {
  if (!pageSchema?.properties) {
    return [];
  }

  return Object.entries(pageSchema.properties).filter(
    ([propName, propSchema]) => {
      if (propSchema?.type === 'object' || propSchema?.type === 'array') {
        return false;
      }
      return showReviewField(
        propName,
        pageSchema,
        pageUiSchema,
        formData,
        formContext,
      );
    },
  );
}

export function schemaPageHasReviewContent(
  page,
  pageState,
  formData,
  formContext,
) {
  if (!pageState) {
    return false;
  }

  const { pageSchema, pageUiSchema, pageData, arrayFields } =
    getSchemaPageContent(page, pageState, formData);

  if (arrayFields.length > 0) {
    return true;
  }

  if (!pageSchema?.properties) {
    return false;
  }

  return Object.keys(pageSchema.properties).some(propName =>
    showReviewField(propName, pageSchema, pageUiSchema, pageData, formContext),
  );
}
